import { AxiosResponse, Method } from 'axios';
import { axiosRequest } from '@/common/api/instance';
import { Meta, RequestConfig, Response as ApiResponse } from '@/types/api/response.types';
import {
  BoqPosition,
  BulkDeleteLineItemDto,
  CreateItem,
  CreateSection,
  CreateTemplate,
  UpdateItem,
  UpdateVariation,
} from '@/types/api/boqs.types';
import { BOQ, BOQStatus } from '@/types/api/sites.types';
import type { BoqLineSet } from '@/types/api/boq-lines.types';

const baseResource = '/boqs';

const makeRequest = async (
  method: Method,
  id: string,
  resource: string,
  position: number[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  path?: number[],
): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  // Convert the position array to the required query string format
  const params = new URLSearchParams();
  if (position) {
    for (const pos of position) params.append('position', pos.toString());
  }
  if (path) {
    for (const pos of path) params.append('path', pos.toString());
  }

  // Construct the URL with the query parameters
  const url = `${baseResource}/${id}/${resource}?${params.toString()}`;

  return axiosRequest().request({
    url,
    method,
    data: data,
  });
};

export const createSection = (data: CreateSection): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  const { id, position, data: requestData } = data;
  return makeRequest('POST', id, 'sections', position, requestData);
};

export const updateSection = (data: CreateSection): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  const { id, position, data: requestData } = data;
  return makeRequest('PUT', id, 'sections', position, requestData);
};

export const deleteSection = (data: BoqPosition): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  return makeRequest('DELETE', data.id, 'sections', data.position);
};

export const createLineItem = (data: CreateItem): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  const { id, position, data: requestData } = data;
  return makeRequest('POST', id, 'line-items', position, requestData);
};

export const updateLineItem = (data: UpdateItem): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  const { id, path, data: requestData } = data;
  return makeRequest('PUT', id, 'line-items', undefined, requestData, path);
};

export const bulkDeleteLineItem = (
  id: string,
  data: BulkDeleteLineItemDto[],
): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  return axiosRequest().post(`${baseResource}/${id}/bulk/line-item-delete`, data);
};

export const updateVariationStatus = (
  data: UpdateVariation,
): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  const { id, path, data: requestData } = data;
  return makeRequest('PATCH', id, 'variations', undefined, requestData, path);
};

export const updateStatus = (
  id: string,
  status: BOQStatus,
  reviewNote?: string,
): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest().patch(`${baseResource}/${id}/status`, {
    status,
    ...(reviewNote ? { reviewNote } : {}),
  });
};

export const saveBoqAsTemplate = (
  data: CreateTemplate,
): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  return axiosRequest().post(`${baseResource}/templates`, data);
};

export const deleteBoqTemplate = (id: string): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest().delete(`${baseResource}/templates/${id}`);
};

// Context: the retry handler drops a replayed export's file, so a retry only adds its dialog to the
// header's failure toast. A logged-in 404 is listed so it takes the toast the default path skips.
const exportConfig: Partial<RequestConfig> = {
  responseType: 'blob',
  skipNetworkRetry: true,
  handlesErrors: [404],
};

export const exportBoq = (id: string, selling: boolean): Promise<AxiosResponse<Blob>> => {
  return axiosRequest().post(`${baseResource}/${id}/export`, { selling }, exportConfig);
};

export const deleteBoq = (id: string): Promise<AxiosResponse<Meta>> => {
  return axiosRequest().delete(`${baseResource}/${id}`);
};

/**
 * `GET /sites/{id}/boqs/lines` — a site's BOQ flattened to its line set (PE-667).
 *
 * Added to the **existing** BOQ service rather than a bulk-namespaced copy, so the standard order
 * flow can adopt the projected read later: it is the same question that flow asks, with the
 * collections that make a production bill several megabytes dropped.
 *
 * Context: read **per picker open**, for one site at a time. That is affordable precisely because
 * of the projection — the unprojected document is ~3 MB — and it is why no store rewrite was needed
 * to make the single-site `boqs` store serve a cross-site flow (D-03).
 *
 * Lines come back in the bill's own order and **nothing is withheld on its draw standing**. Read
 * `drawDataAvailable` once before rendering any draw treatment: draws are sparsely populated, so
 * "no draw data anywhere" is the common case and is a different answer from "this line is not due".
 */
export const getSiteBoqLines = async (
  siteId: string,
  signal?: AbortSignal,
): Promise<AxiosResponse<ApiResponse<BoqLineSet>>> => {
  return axiosRequest().get(`/sites/${siteId}/boqs/lines`, { signal });
};
